import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = [
    {
      id: 'landing',
      label: 'Home',
      path: '/landing-page',
      icon: 'Home',
      color: 'accent',
      description: 'Discover all three professional worlds'
    },
    {
      id: 'community',
      label: 'Community Guy',
      path: '/community-guy-world',
      icon: 'Users',
      color: 'success',
      description: 'Community leadership & event management'
    },
    {
      id: 'web3',
      label: 'Web3 Developer',
      path: '/web3-developer-world',
      icon: 'Zap',
      color: 'warning',
      description: 'Blockchain development & DeFi projects'
    },
    {
      id: 'software',
      label: 'Software Developer',
      path: '/software-developer-world',
      icon: 'Code',
      color: 'primary',
      description: 'Full-stack development & technical expertise'
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const getCurrentWorld = () => {
    const currentItem = navigationItems.find(item => item.path === location.pathname);
    return currentItem || navigationItems[0];
  };

  const currentWorld = getCurrentWorld();

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-100 transition-all duration-200 ${
          isScrolled 
            ? 'bg-surface/95 backdrop-blur-sm shadow-elevation-1' 
            : 'bg-surface'
        }`}
      >
        <div className="px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <div 
              className="flex items-center space-x-3 cursor-pointer group"
              onClick={() => handleNavigation('/landing-page')}
            >
              <div className="relative">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center transform group-hover:scale-105 transition-transform duration-200">
                  <span className="text-surface mt-1 font-heading font-bold text-lg md:text-3xl">N</span>
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-accent rounded-full animate-pulse"></div>
              </div>
              <div className="hidden sm:block">
                <h1 className="font-heading font-bold text-lg md:text-xl text-primary group-hover:text-accent transition-colors duration-200">
                  Nikunj
                </h1>
                <p className="font-caption text-xs text-text-secondary -mt-1">
                  Multi-dimensional developer
                </p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.path)}
                    className={`group relative px-4 py-2 rounded-md transition-all duration-200 ${
                      isActive
                        ? `bg-${item.color}/10 text-${item.color}`
                        : 'text-text-secondary hover:text-primary hover:bg-background'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <Icon 
                        name={item.icon} 
                        size={18} 
                        className={`transition-colors duration-200 ${
                          isActive ? `text-${item.color}` : 'text-text-secondary group-hover:text-primary'
                        }`}
                      />
                      <span className="font-body font-medium text-sm">
                        {item.label}
                      </span>
                    </div>
                    {isActive && (
                      <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 h-0.5 bg-${item.color} rounded-full`}></div>
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-md text-text-secondary hover:text-primary hover:bg-background transition-colors duration-200"
              aria-label="Toggle mobile menu"
            >
              <Icon 
                name={isMobileMenuOpen ? 'X' : 'Menu'} 
                size={24} 
                className="transition-transform duration-200"
              />
            </button>
          </div>
        </div>

        
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-150 lg:hidden">
          <div 
            className="absolute inset-0 bg-primary/20 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
          <div className="absolute top-16 md:top-20 left-0 right-0 bg-surface shadow-elevation-3 border-t border-border/20">
            <nav className="px-4 py-6">
              <div className="space-y-1">
                {navigationItems.map((item, index) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNavigation(item.path)}
                      className={`w-full text-left p-4 rounded-lg transition-all duration-200 ${
                        isActive
                          ? `bg-${item.color}/10 border border-${item.color}/20`
                          : 'hover:bg-background border border-transparent'
                      }`}
                      style={{
                        animationDelay: `${index * 100}ms`,
                        animation: 'slideUp 0.4s ease-in-out forwards'
                      }}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-md ${
                          isActive ? `bg-${item.color}/20` : 'bg-background'
                        }`}>
                          <Icon 
                            name={item.icon} 
                            size={20} 
                            className={isActive ? `text-${item.color}` : 'text-text-secondary'}
                          />
                        </div>
                        <div className="flex-1">
                          <div className={`font-body font-medium ${
                            isActive ? `text-${item.color}` : 'text-primary'
                          }`}>
                            {item.label}
                          </div>
                          <div className="font-caption text-xs text-text-secondary mt-1">
                            {item.description}
                          </div>
                        </div>
                        {isActive && (
                          <Icon 
                            name="ChevronRight" 
                            size={16} 
                            className={`text-${item.color}`}
                          />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;